import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import type { HelpPanelContextValue } from "@/interfaces/help";

const HelpPanelContext = createContext<HelpPanelContextValue | null>(null);

/**
 * Owns whether the Help Center slide-over is open. Renders nothing — `components/help/HelpPanel`
 * draws the panel and reacts to Escape itself.
 */
export function HelpPanelProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [openSlug, setOpenSlug] = useState<string | null>(null);

  const open = useCallback((slug?: string) => {
    setOpenSlug(slug ?? null);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
    setOpenSlug(null);
  }, []);
  const toggle = useCallback(() => {
    setOpenSlug(null);
    setIsOpen((wasOpen) => !wasOpen);
  }, []);

  const value = useMemo<HelpPanelContextValue>(
    () => ({ isOpen, openSlug, open, close, toggle }),
    [isOpen, openSlug, open, close, toggle],
  );

  return <HelpPanelContext.Provider value={value}>{children}</HelpPanelContext.Provider>;
}

export function useHelpPanel(): HelpPanelContextValue {
  const ctx = useContext(HelpPanelContext);
  if (!ctx) throw new Error("useHelpPanel must be used within HelpPanelProvider");
  return ctx;
}
